/**
 * Manages panel navigation, tracks opened panels, and handles
 * save/discard prompts when switching or closing panels.
 */
import type { BasePanel } from "./BasePanel";
import type { AnnotationEditorPanel } from "./AnnotationEditorPanel";

const DEFAULT_CONFIRM_MESSAGE = "You have unsaved changes. Are you sure you want to leave?";

export interface OpenPanelOptions {
    parentPanelId: string;
}

export interface RegisterPanelOptions {
    parentPanelId: string;
}

export class PanelManager {
    private readonly _panels = new Map<string, BasePanel>();

    private readonly _openedPanels = new Set<BasePanel>();

    private _activePanel: BasePanel | null = null;

    private readonly _panelParents = new Map<string, string>(); // childId -> parentId

    private _confirmMessage = DEFAULT_CONFIRM_MESSAGE;

    private readonly _beforeNavigateChecks: Array<() => Promise<boolean>> = [];

    /** Add a check that runs before any navigation (e.g. project.annotationModified) */
    addBeforeNavigateCheck(check: () => Promise<boolean>): void {
        this._beforeNavigateChecks.push(check);
    }

    /** Register a panel. Optionally specify parent for child panels. */
    registerPanel(panel: BasePanel, options?: RegisterPanelOptions): void {
        const id = panel.getId();

        this._panels.set(id, panel);

        if (options?.parentPanelId) {
            this._panelParents.set(id, options.parentPanelId);
        }
    }

    /** Check if a panel is currently open */
    isPanelOpen(panelId: string): boolean {
        const panel = this._panels.get(panelId);

        return panel ? this._openedPanels.has(panel) : false;
    }

    /** Get the panel instance by ID */
    getPanel(panelId: string): BasePanel | undefined {
        return this._panels.get(panelId);
    }

    /** Get the AnnotationEditorPanel instance (type-safe direct access). */
    getAnnotationEditorPanel(): AnnotationEditorPanel | undefined {
        return this.getPanel("annotation-editor") as AnnotationEditorPanel | undefined;
    }

    /** Open a panel. Returns false if navigation was blocked (e.g. user cancelled discard). */
    async openPanel(panelId: string, options?: OpenPanelOptions): Promise<boolean> {
        const panel = this._panels.get(panelId);

        if (!panel) {
            console.warn(`PanelManager: unknown panel "${panelId}"`);

            return false;
        }

        const parentId = options?.parentPanelId;

        if (parentId && !this.isPanelOpen(parentId)) {
            console.warn(`PanelManager: cannot open child "${panelId}" - parent "${parentId}" is not open`);

            return false;
        }

        const canNavigate = await this.canNavigateAway();

        if (!canNavigate) {
            return false;
        }

        if (parentId) {
            this._panelParents.set(panelId, parentId);
            panel.setParentPanel(this._panels.get(parentId) ?? null);
        } else {
            this.hideAllPopup();
            this.hideAllPanels();
            this._openedPanels.clear();
            panel.setParentPanel(null);
        }

        panel.init();
        panel.show();
        this._openedPanels.add(panel);
        this._activePanel = panel;

        return true;
    }

    /** Open panel synchronously (for cases where async is not needed). Use when canNavigateAway is not required. */
    openPanelSync(panelId: string, options?: OpenPanelOptions): boolean {
        const panel = this._panels.get(panelId);

        if (!panel) {
            console.warn(`PanelManager: unknown panel "${panelId}"`);

            return false;
        }

        const parentId = options?.parentPanelId;

        if (parentId && !this.isPanelOpen(parentId)) {
            return false;
        }

        if (parentId) {
            this._panelParents.set(panelId, parentId);
            panel.setParentPanel(this._panels.get(parentId) ?? null);
        } else {
            this.hideAllPopup();
            this.hideAllPanels();
            this._openedPanels.clear();
            panel.setParentPanel(null);
        }

        panel.init();
        panel.show();
        this._openedPanels.add(panel);
        this._activePanel = panel;

        return true;
    }

    /** Close a panel. Parent stays open. Recursively closes children. */
    async closePanel(panelId: string): Promise<void> {
        const canNavigate = await this.canNavigateAway();

        if (!canNavigate) {
            return;
        }

        this.closePanelInternal(panelId);
    }

    /** Close panel without prompting (use when user has already confirmed) */
    closePanelInternal(panelId: string): void {
        const children = [...this._panelParents.entries()]
            .filter(([, parentId]) => parentId === panelId)
            .map(([childId]) => childId);

        children.forEach((childId) => this.closePanelInternal(childId));

        const panel = this._panels.get(panelId);

        if (panel) {
            panel.hide();
            panel.setParentPanel(null);
            this._openedPanels.delete(panel);

            if (this._activePanel === panel) {
                this._activePanel = null;
            }
        }

        this._panelParents.delete(panelId);
    }

    /** Close all panels */
    async closeAll(): Promise<void> {
        const canNavigate = await this.canNavigateAway();

        if (!canNavigate) {
            return;
        }

        [...this._openedPanels].forEach((panel) => panel.hide());

        this._openedPanels.clear();
        this._activePanel = null;
        this._panelParents.clear();
    }

    /** Hide all panels without closing (for switching). Does not prompt. */
    hideAllPanels(): void {
        [...this._openedPanels].forEach((panel) => panel.hide());

        this._activePanel = null;
    }

    /** Hide all DOM popup wrappers (legacy integration). */
    hideAllPopup(): void {
        if (typeof window !== "undefined" && window.jQuery) {
            window.jQuery(".popup-wrapper").hide();
            window.jQuery(".transform-has-modal-next").next().addClass("hidden");
        }
    }

    /**
     * Check if we can navigate away from current panels.
     * Prompts for unsaved changes. Returns true if OK to proceed.
     */
    async canNavigateAway(): Promise<boolean> {
        const checkResults = await Promise.all(this._beforeNavigateChecks.map((check) => check()));

        if (checkResults.some((ok) => !ok)) {
            return false;
        }

        const panels = [...this._openedPanels];

        for (let i = 0; i < panels.length; i++) {
            const panel = panels[i];

            if (panel.hasUnsavedChanges && panel.hasUnsavedChanges()) {
                if (!panel.confirmDiscardChanges || !panel.confirmDiscardChanges()) {
                    return false;
                }
            }
        }

        return true;
    }

    /** Get currently active panel */
    getActivePanel(): BasePanel | null {
        return this._activePanel;
    }

    /** Get all open panels */
    getOpenedPanels(): BasePanel[] {
        return [...this._openedPanels];
    }

    /** Set custom confirmation message for unsaved changes */
    setConfirmMessage(message: string): void {
        this._confirmMessage = message;
    }

    /** Get the confirmation message */
    getConfirmMessage(): string {
        return this._confirmMessage;
    }
}
